import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckIcon, CircleDashedIcon } from 'lucide-react';
import { FilterChip } from '../components/FilterChip';
import { MetricRow } from '../components/MetricRow';
import { PageHeader } from '../components/PageHeader';
import { SportEvolutionChart } from '../components/SportEvolutionChart';
import { Card, SectionTitle } from '../components/ui/Card';
import { strengthMilestones } from '../data/insights';
import { metricsForSport, sports } from '../data/sportMetrics';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import type { SportId } from '../types';
export function SportMetrics() {
  const {
    sport: param
  } = useParams();
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const metrics = useMetrics();
  const sport = (sports.includes(param as SportId) ? param : 'running') as SportId;
  const list = metricsForSport(sport);
  return <div className="px-2 pb-2" data-id="element-4022" data-mp="2qel3flbgy54t">
      <PageHeader title={t(`category.${sport}`)} subtitle={t('metrics.sports.subtitle')} showBack data-id="element-4023" data-mp="2qdjnp3jicugr" />

      <ul className="bee-rail -mx-2 flex gap-1 overflow-x-auto px-2 pb-1" role="tablist" data-id="element-4024" data-mp="2lgb2sv6z6cbt">
        {sports.map(item => <li key={item} className="shrink-0" data-id="element-4025" data-mp="2cetrdq4iiytg">
            <FilterChip label={t(`category.${item}`)} active={sport === item} onClick={() => navigate(`/perfil/esportes/${item}`)} data-id="element-4026" data-mp="2cds6ktxp2xyj" />
          </li>)}
      </ul>

      <Card className="mt-2 p-2" data-id="element-4027" data-mp="22npc0paxdby3">
        <SectionTitle data-id="element-4028" data-mp="1xpxcpa7yanjv">{t('metrics.units')}</SectionTitle>
        <ul className="flex gap-1" data-id="element-4029" data-mp="1xqgqceqkips3">
          {(['metric', 'imperial'] as const).map(system => <li key={system} data-id="element-4030" data-mp="1ssf7nux20sh5">
              <FilterChip label={t(`metrics.units.${system}`)} active={metrics.unitSystem === system} onClick={() => metrics.setUnitSystem(system)} data-id="element-4031" data-mp="1op96v2spnti2" />
            </li>)}
        </ul>
      </Card>

      <Card className="mt-2 px-2" data-id="element-4032" data-mp="1a0z50p5wkt39">
        <ul data-id="element-4033" data-mp="1e45jgk365qkt">
          {list.map(metric => <MetricRow key={metric.id} label={tl(metric.label)} kind={metric.kind} value={metric.value} hint={metric.value === 0 ? t('metrics.noRecord') : undefined} onOpen={metric.chart ? () => navigate(`/perfil/grafico/${encodeURIComponent(metric.id)}`) : undefined} data-id="element-4034" data-mp="1e4x10qzxlns3" />)}
        </ul>
      </Card>

      <SportEvolutionChart key={sport} sport={sport} data-id="element-4035" data-mp="1j3qe4ibb3crq" />

      {sport === 'strength' && <section className="mt-3" data-id="element-4036" data-mp="1o1rzj9w7fo6d">
          <SectionTitle data-id="element-4037" data-mp="1o10dapohz2sr">{t('metrics.milestones')}</SectionTitle>
          <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-4038" data-mp="2brdybw8o01du">
            {strengthMilestones.map(milestone => <li key={milestone.id} className="flex items-center gap-2 border-b border-sand-line py-2 last:border-b-0" data-id="element-4039" data-mp="2gk72ahwb75ov">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-pill ${milestone.reached ? 'bg-gold text-deep' : 'bg-sand-deep text-muted'}`} data-id="element-4040" data-mp="5c37oeenlkn">
                  {milestone.reached ? <CheckIcon size={15} strokeWidth={3} data-id="element-4041" data-mp="3iayjm6zinnq4" /> : <CircleDashedIcon size={15} data-id="element-4042" data-mp="3dc42d9h76784" />}
                </span>
                <span className="min-w-0 flex-1" data-id="element-4043" data-mp="3rb3q8ewvyi38">
                  <span className="block text-[13px] font-medium text-deep" data-id="element-4044" data-mp="3r8r0vgost5g5">
                    {tl(milestone.label)}
                  </span>
                  <span className="bee-num mt-[2px] block text-[11px] text-muted" data-id="element-4045" data-mp="16k36fyr03ihu">
                    {tl(milestone.detail)}
                  </span>
                </span>
              </li>)}
          </ul>
        </section>}

      <p className="mt-2 px-1 text-[11px] leading-snug text-muted" data-id="element-4046" data-mp="1wxj1uxmetkpv">
        {t('metrics.sports.note')}
      </p>
    </div>;
}